<template>
  <InvitationQuotaCsvMapping
    v-if="csvPreviewData"
    :invitation-quota="invitationQuota"
    :csv-preview-data="csvPreviewData"
    :saving="saving"
    @save-mapping="saveMapping"
  />
</template>

<script lang="ts" setup>
import { computed, inject, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { supportedKinds } from '@/components/InvitationQuotaCsvUpload'
import type { CsvMappingAttributes, ImportInviteesMutation, InvitationQuota } from '@/gql/myomr'
import { useCsvPreviewDataQuery, useImportInviteesMutation } from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'
import { reloadQuotaKey } from '@/symbols/reloadQuotaKey'

import InvitationQuotaCsvMapping from './InvitationQuotaCsvMapping.vue'

const props = defineProps<{
  invitationQuota: InvitationQuota
  loading: boolean
  uploadId: string
}>()

const router = useRouter()
const { t, te } = useI18n()

const reloadQuota = inject(reloadQuotaKey) as () => void

const isSupportedKind = computed(() => supportedKinds.includes(props.invitationQuota.kind))
const { result, onError: onQueryError } = useCsvPreviewDataQuery(
  {
    csvUploadId: props.uploadId,
    invitationQuotaId: props.invitationQuota.id,
  },
  () => ({ enabled: isSupportedKind.value }),
)
const csvPreviewData = computed(() => result.value?.csvPreviewData)

const { mutate, loading: saving, onDone, onError: onMutationError } = useImportInviteesMutation()

const saveMapping = ({ mapping, hasHeaders }: { mapping: CsvMappingAttributes; hasHeaders: boolean }) => {
  mutate({
    invitationQuotaId: props.invitationQuota.id,
    csvUploadId: props.uploadId,
    hasHeaders,
    mapping,
  })
}

function fallbackSuccessMessage() {
  const successKey = `EmailInvitationQuota.mapping.${props.invitationQuota.kind}.successful`

  return te(successKey) ? t(successKey) : t('EmailInvitationQuota.mapping.successful')
}

function showImportNotification(importInvitees?: ImportInviteesMutation['importInvitees']) {
  if (importInvitees?.errors.length) {
    showNotification(importInvitees.errors.join(', '), 'error')
    return
  }

  if (importInvitees?.withErrorsCount && importInvitees.withoutErrorsCount) {
    showNotification(
      t('EmailInvitationQuota.mapping.import.warning', {
        withErrorsCount: importInvitees.withErrorsCount,
        withoutErrorsCount: importInvitees.withoutErrorsCount,
      }).toString(),
      'warning',
    )
  } else if (importInvitees?.withErrorsCount) {
    showNotification(t('EmailInvitationQuota.mapping.import.error', importInvitees.withErrorsCount).toString(), 'error')
  } else if (importInvitees?.withoutErrorsCount) {
    showNotification(t('EmailInvitationQuota.mapping.import.success', importInvitees.withoutErrorsCount).toString(), 'success')
  } else {
    showNotification(fallbackSuccessMessage().toString(), 'success')
    return
  }

  if (importInvitees.withoutErrorsCount) {
    showNotification(fallbackSuccessMessage().toString(), 'success')
  }
}

onDone((result) => {
  showImportNotification(result.data?.importInvitees)
  reloadQuota()

  router.push({ name: 'invitationQuota' })
})

onMutationError((error) => {
  if (error) showNotification(error.message, 'error')
})
onQueryError((error) => {
  if (error) showNotification(error.message, 'error')
})

onMounted(() => {
  if (!isSupportedKind.value) {
    router.push({ name: 'invitationQuota' })
  }
})
</script>
